import { CKEditor } from "ckeditor4-react";
import { Breadcrumb, CardContainer, Container } from "components";
import { useFormik } from "formik";
import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL_organization } from "constants/api";
import { organizationReducers } from "redux/organizationSlice";
import { postData, putData } from "actions";

const FormOrganization = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      phone: location.state ? location.state.item.phone : 0,
      website: location.state ? location.state.item.website : "",
      address: location.state ? location.state.item.address : false,
      internal_notes: location.state
        ? location.state.item.internal_notes
        : false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string().required("Phone is required"),
      website: Yup.string().required("Website is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_organization,
          formData,
          { dispatch, redux: organizationReducers },
          "ADD_ORGANIZATION",
          () => {
            navigate("/users/organizations");
          }
        );
      } else {
        putData(
          `${API_URL_organization}${location.state.item.id}/`,
          formData,
          { dispatch, redux: organizationReducers },
          "ADD_ORGANIZATION",
          () => {
            navigate("/users/organizations");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (typeForm === "create") {
      formik.setFieldValue("name", "");
      formik.setFieldValue("phone", "");
      formik.setFieldValue("website", "");
      formik.setFieldValue("address", "");
      formik.setFieldValue("internal_notes", "");
    }
  }, [typeForm]);

  return (
    <Container>
      <Breadcrumb title={"Form Organization"} excludedPaths={["users"]} />
      <CardContainer
        title={
          typeForm === "create" ? "Create Organization" : "Edit Organization"
        }
      >
        <form onSubmit={handleFormSubmit}>
          <div className="m-5">
            <div className="grid grid-cols-12 grid-flow-row mb-4 gap-4">
              <div className=" col-span-12 md:col-span-4">
                <div className="grid grid-cols-12">
                  <label className="col-span-12 font-bold">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                {formik.touched.name && formik.errors.name && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.name}
                  </div>
                )}
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    type="number"
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.phone}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">
                    Website <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="website"
                    name="website"
                    type="url"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.website && formik.errors.website && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.website}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 grid-flow-row mb-4 gap-4">
              <div className=" col-span-12 md:col-span-6">
                <div className="grid grid-cols-12">
                  <label className="col-span-12 font-bold">Address</label>
                  <div className="col-span-12">
                    <CKEditor
                      config={toolbarConfig}
                      type="classic"
                      initData={formik.values.address}
                      onChange={(e) =>
                        formik.setFieldValue("address", e.editor.getData())
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">Internal note</label>
                  <div className="col-span-12">
                    <CKEditor
                      config={toolbarConfig}
                      type="classic"
                      initData={formik.values.internal_notes}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "internal_notes",
                          e.editor.getData()
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="h-14 w-full flex items-center px-5 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="rounded-md text-white py-1.5 w-32 bg-sky-700 hover:bg-sky-800 "
            >
              Submit
            </button>
          </div>
        </form>
      </CardContainer>
    </Container>
  );
};

export default FormOrganization;
